import type {
  ApiBalanceBySlug,
  ApiBaseCurrency,
  ApiChain,
  ApiCurrencyRates,
  ApiTokenWithPrice,
} from '../../api/types';
import type { Account, AccountSettings, AccountState, GlobalState, UserToken } from '../types';

import {
  PRICELESS_TOKEN_HASHES,
  TINY_TRANSFER_MAX_COST,
  TONCOIN,
} from '../../config';
import { parseAccountId } from '../../util/account';
import { calculateTokenPrice } from '../../util/calculatePrice';
import { getDefaultEnabledSlugs } from '../../util/chain';
import { toBig } from '../../util/decimals';
import memoize from '../../util/memoize';
import { round } from '../../util/round';
import { sortTokens } from '../../util/tokens';
import withCache from '../../util/withCache';
import {
  selectAccount,
  selectAccountSettings,
  selectAccountState,
  selectCurrentAccountId,
  selectCurrentAccountState,
} from './accounts';

const EMPTY_BALANCES: ApiBalanceBySlug = {};

function getHasConfirmedActivities(activities: AccountState['activities']) {
  const confirmedCount = (activities?.idsMain?.length ?? 0)
    - (activities?.localActivityIds?.length ?? 0)
    - Object.values(activities?.pendingActivityIds ?? {}).reduce<number>((sum, ids) => sum + (ids?.length ?? 0), 0);
  return confirmedCount > 0;
}

function getAreAllBalancesNearZero(balancesBySlug: ApiBalanceBySlug, tokenInfo: GlobalState['tokenInfo']) {
  return Object.entries(balancesBySlug).every(([slug, balance]) => {
    const info = tokenInfo.bySlug[slug];

    // If token info is missing, treat it as zero-value
    if (!info) return true;

    const balanceBig = toBig(balance, info.decimals);
    return balanceBig.mul(info.priceUsd ?? 0).lt(TINY_TRANSFER_MAX_COST);
  });
}

export const selectAccountTokensMemoizedFor = withCache((accountId: string) => memoize((
  balancesBySlug: ApiBalanceBySlug,
  tokenInfo: GlobalState['tokenInfo'],
  accountSettings: AccountSettings = {},
  areTokensWithNoCostHidden: boolean = false,
  baseCurrency: ApiBaseCurrency,
  currencyRates: ApiCurrencyRates,
  hasActivities: boolean,
  accountByChain: Account['byChain'],
) => {
  const { network } = parseAccountId(accountId);
  const shouldShowOnlyDefaultTokens = !hasActivities && getAreAllBalancesNearZero(balancesBySlug, tokenInfo);
  // Balances are seeded for every default slug regardless of the account, so a single-chain account
  // would otherwise be offered rows it cannot use
  const defaultEnabledSlugs = getDefaultEnabledSlugs(
    network, Object.keys(accountByChain) as ApiChain[],
  );
  const pinnedSlugs = accountSettings.pinnedSlugs ?? [];

  const tokens = Object
    .entries(balancesBySlug)
    .filter(([slug]) => {
      const token = tokenInfo.bySlug[slug];
      return token && token.chain in accountByChain
        && !accountSettings.deletedSlugs?.includes(slug);
    })
    .map(([slug, balance]): UserToken => {
      const {
        symbol, name, localizedName, image, decimals, cmcSlug, color, chain, tokenAddress, codeHash,
        type, label, keywords, percentChange24h = 0, priceUsd,
      } = tokenInfo.bySlug[slug];

      const price = calculateTokenPrice(priceUsd ?? 0, baseCurrency, currencyRates);
      const balanceBig = toBig(balance, decimals);
      const totalValue = balanceBig.mul(price).round(decimals).toString();
      const hasCost = balanceBig.mul(priceUsd ?? 0).gte(TINY_TRANSFER_MAX_COST);
      const isPricelessTokenWithBalance = PRICELESS_TOKEN_HASHES.has(codeHash!) && balance > 0n;

      // The default tokens stay listed whatever they are worth: they are the coins the wallet is for,
      // and hiding an empty one leaves the user without the row they came to fund. Everything else has
      // to earn its place with a balance worth showing.
      const isEnabled = accountSettings.alwaysShownSlugs?.includes(slug)
        || defaultEnabledSlugs.has(slug)
        || (!shouldShowOnlyDefaultTokens
          && (hasCost || isPricelessTokenWithBalance || (!areTokensWithNoCostHidden && balance > 0n)));

      const isDisabled = !isEnabled || accountSettings.alwaysHiddenSlugs?.includes(slug);

      return {
        chain,
        symbol,
        slug,
        amount: balance,
        name,
        localizedName,
        image,
        price,
        priceUsd,
        decimals,
        change24h: round(percentChange24h / 100, 4),
        isDisabled,
        cmcSlug,
        totalValue,
        color,
        tokenAddress,
        codeHash,
        type,
        label,
        keywords,
      };
    });

  return sortTokens(tokens, pinnedSlugs);
}));

export function selectCurrentAccountTokens(global: GlobalState) {
  const accountId = selectCurrentAccountId(global);
  return accountId ? selectAccountTokens(global, accountId) : undefined;
}

export function selectCurrentAccountTokenBalance(global: GlobalState, slug: string) {
  return selectCurrentAccountState(global)?.balances?.bySlug[slug] ?? 0n;
}

export function selectCurrentToncoinBalance(global: GlobalState) {
  return selectCurrentAccountTokenBalance(global, TONCOIN.slug);
}

export function selectAccountTokens(global: GlobalState, accountId: string) {
  const accountState = selectAccountState(global, accountId);
  const balancesBySlug = accountState?.balances?.bySlug;
  const account = selectAccount(global, accountId);
  if (!balancesBySlug || !global.tokenInfo || !account) {
    return undefined;
  }

  const accountSettings = selectAccountSettings(global, accountId);
  const { areTokensWithNoCostHidden, baseCurrency } = global.settings;
  return selectAccountTokensMemoizedFor(accountId)(
    balancesBySlug,
    global.tokenInfo,
    accountSettings,
    areTokensWithNoCostHidden,
    baseCurrency,
    global.currencyRates,
    getHasConfirmedActivities(accountState?.activities),
    account.byChain,
  );
}

export function selectAccountTokenBySlug(global: GlobalState, slug: string) {
  const accountTokens = selectCurrentAccountTokens(global);
  return accountTokens?.find((token) => token.slug === slug);
}

export function selectToken(global: GlobalState, slug: string) {
  return global.tokenInfo.bySlug[slug];
}

export const selectUserTokenMemoized = memoize((global: GlobalState, slug: string): UserToken | undefined => {
  const apiToken = selectToken(global, slug);
  if (!apiToken) return undefined;

  const amount = selectCurrentAccountTokenBalance(global, slug);

  return buildUserTokenFromTokenInfo(
    apiToken,
    amount,
    global.settings.baseCurrency,
    global.currencyRates,
  );
});

const selectTokenInfoUserTokensMemoized = memoize((
  tokensBySlug: GlobalState['tokenInfo']['bySlug'],
  balancesBySlug: ApiBalanceBySlug,
  baseCurrency: ApiBaseCurrency,
  currencyRates: ApiCurrencyRates,
): UserToken[] => {
  return Object.values(tokensBySlug)
    .map((token) => buildUserTokenFromTokenInfo(
      token,
      balancesBySlug[token.slug] ?? 0n,
      baseCurrency,
      currencyRates,
    ))
    .sort(compareTokenInfoUserTokens);
});

export function selectTokenInfoUserTokens(global: GlobalState) {
  const accountId = selectCurrentAccountId(global);
  if (!accountId || !global.tokenInfo) {
    return undefined;
  }

  return selectTokenInfoUserTokensMemoized(
    global.tokenInfo.bySlug,
    selectCurrentAccountState(global)?.balances?.bySlug ?? EMPTY_BALANCES,
    global.settings.baseCurrency,
    global.currencyRates,
  );
}

export function selectTokenByMinterAddress(global: GlobalState, minter: string) {
  return Object.values(global.tokenInfo.bySlug).find((token) => token.tokenAddress === minter);
}

const selectHasLocalizedTokenNamesMemoized = memoize((tokensBySlug: GlobalState['tokenInfo']['bySlug']) => {
  for (const slug in tokensBySlug) {
    if (tokensBySlug[slug].localizedName) {
      return true;
    }
  }

  return false;
});

export function selectHasLocalizedTokenNames(global: GlobalState) {
  return selectHasLocalizedTokenNamesMemoized(global.tokenInfo.bySlug);
}

export function selectChainTokenWithMaxBalanceSlow(global: GlobalState, chain: ApiChain): UserToken | undefined {
  return (selectCurrentAccountTokens(global) ?? [])
    .filter((token) => token.chain === chain)
    .reduce((maxToken, currentToken) => {
      const currentBalance = currentToken.priceUsd * Number(currentToken.amount);
      const maxBalance = maxToken ? maxToken.priceUsd * Number(maxToken.amount) : 0;

      return currentBalance > maxBalance ? currentToken : maxToken;
    });
}

function buildUserTokenFromTokenInfo(
  token: ApiTokenWithPrice,
  amount: bigint,
  baseCurrency: ApiBaseCurrency,
  currencyRates: ApiCurrencyRates,
): UserToken {
  const priceUsd = token.priceUsd ?? 0;
  const price = calculateTokenPrice(priceUsd, baseCurrency, currencyRates);

  return {
    ...token,
    amount,
    price,
    priceUsd,
    change24h: round((token.percentChange24h ?? 0) / 100, 4),
    totalValue: toBig(amount, token.decimals).mul(price).toString(),
  };
}

function compareTokenInfoUserTokens(a: UserToken, b: UserToken) {
  return a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase())
    || a.symbol.trim().toLowerCase().localeCompare(b.symbol.trim().toLowerCase());
}

export function selectMultipleAccountsTokensSlow(
  networkAccounts: Record<string, Account> | undefined,
  byAccountId: GlobalState['byAccountId'],
  tokenInfo: GlobalState['tokenInfo'],
  settingsByAccountId: Record<string, AccountSettings>,
  areTokensWithNoCostHidden: boolean | undefined,
  baseCurrency: ApiBaseCurrency,
  currencyRates: ApiCurrencyRates,
) {
  const result: Record<string, UserToken[] | undefined> = {};
  if (!networkAccounts || !tokenInfo) return result;

  for (const accountId in networkAccounts) {
    const accountState = byAccountId[accountId];
    const balancesBySlug = accountState?.balances?.bySlug;
    if (!balancesBySlug) {
      result[accountId] = undefined;
      continue;
    }

    const accountSettings = settingsByAccountId[accountId];
    result[accountId] = selectAccountTokensMemoizedFor(accountId)(
      balancesBySlug,
      tokenInfo,
      accountSettings,
      areTokensWithNoCostHidden,
      baseCurrency,
      currencyRates,
      getHasConfirmedActivities(accountState?.activities),
      networkAccounts[accountId].byChain,
    );
  }

  return result;
}
